import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { CertificateMetalValue, MetalComposition } from '../types';
import { getMetalValidationResults, getStatusSummary } from '../utils/statusHelpers';
import SpecWindow from './SpecWindow';
import { statusChipSx, tokens } from '../theme';

interface MetalValuesDisplayProps {
  metalValues: CertificateMetalValue[];
  materialGradeCompositions: MetalComposition[];
}

function MetalValuesDisplay({
  metalValues,
  materialGradeCompositions,
}: MetalValuesDisplayProps): React.ReactElement {
  const rows = getMetalValidationResults(metalValues, materialGradeCompositions);
  const summary = getStatusSummary(rows);

  if (rows.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">Keine Metallwerte erfasst.</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Metallwerte Analyse
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Sollbereich · Ist-Wert · {summary.ok} im Bereich
        {summary.error > 0 ? ` · ${summary.error} außerhalb` : ''}
        {summary.missing > 0 ? ` · ${summary.missing} nicht gemessen` : ''}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))',
          gap: 1.5,
        }}
      >
        {rows.map((metal) => {
          const color =
            metal.status === 'ok'
              ? tokens.ok
              : metal.status === 'error'
                ? tokens.bad
                : tokens.muted;
          return (
            <Box
              key={metal.name}
              sx={{ border: `1px solid ${tokens.border}`, borderRadius: '10px', p: 1.5 }}
            >
              <Typography variant="body2" fontWeight={500}>
                {metal.name}
              </Typography>
              <Typography sx={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.03em', color, my: 0.5 }}>
                {metal.status === 'missing' ? '—' : metal.measuredValue.toFixed(2)}
                {metal.status !== 'missing' && (
                  <Typography component="span" sx={{ fontSize: 12, fontWeight: 400, color: tokens.muted, ml: 0.5 }}>
                    %
                  </Typography>
                )}
              </Typography>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  fontSize: 11,
                  px: 1,
                  py: 0.15,
                  borderRadius: 999,
                  ...statusChipSx(metal.status),
                }}
              >
                {metal.statusMessage}
              </Box>
              <SpecWindow
                min={metal.minValue}
                max={metal.maxValue}
                value={metal.status === 'missing' ? null : metal.measuredValue}
                status={metal.status}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default MetalValuesDisplay;
